import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://8h02jqezvc.execute-api.us-east-1.amazonaws.com/prod';

// Helper function to get authenticated headers
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {
      'Content-Type': 'application/json'
    };
  }
}

// API service using authenticated requests
export const workflowApi = {
  getMetrics: async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/metrics`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  listWorkflows: async (params?: { workflowType?: string; stateMachineArn?: string; status?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.workflowType) queryParams.append('workflowType', params.workflowType);
    if (params?.stateMachineArn) queryParams.append('stateMachineArn', params.stateMachineArn);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const url = `${API_BASE_URL}/api/workflows${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const headers = await getAuthHeaders();
    
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  getWorkflow: async (executionArn: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/workflows/${encodeURIComponent(executionArn)}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  createWorkflow: async (data: {
    workflowType: string;
    input: any;
    schedule?: string;
  }) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/workflows`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return response.json();
  },

  stopWorkflow: async (executionArn: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/workflows/${encodeURIComponent(executionArn)}`, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  createWorkflowDefinition: async (definition: any) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/workflow-definitions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(definition),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Observability methods
  getExecutionSummary: async (workflowType: string = 'data-processing', timeRange: string = '24h') => {
    const headers = await getAuthHeaders();
    const queryParams = new URLSearchParams({
      workflowType,
      timeRange
    });
    
    const response = await fetch(`${API_BASE_URL}/api/observability/execution-summary?${queryParams.toString()}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  },

  getExecutionErrorLogs: async (executionArn: string) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/observability/error-logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ executionArn })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  }
};
